/* eslint-disable @typescript-eslint/no-use-before-define */

import { z } from "zod"
import { useCallback, useState } from "preact/hooks"
import { FORM_ERROR } from "final-form"
import { Field, Form, FormSpy } from "react-final-form"
import { Logo } from "../components/Logo"
import { OfferNote } from "../components/OfferNote"
import { Sidebar } from "../components/Sidebar"
import css from "../style.module.scss"
import { $user } from "../hooks/user"
import { navigate, replace } from "../hooks/navigation"
import { t } from "../hooks/translations"

const signInSchema = z.object({
  email: z.string().email().trim(),
  password: z.string().min(6).trim(),
})

type SignInValues = z.infer<typeof signInSchema>

export const SignInScreen = function SignInScreen() {
  return (
    <div className={css["tablet-screen"]}>
      <Sidebar />
      <div>
        <div className={css["hide-for-tablet"]}>
          <Logo style={{ marginBottom: 16 }} />
          <OfferNote />
        </div>
        <SignIn />
      </div>
    </div>
  )
}

const SignIn = function SignIn() {
  const [initialValues] = useState<SignInValues>(() => ({
    email: $user.get().email,
  }))

  const onSubmit = useCallback(async (values: SignInValues) => {
    const result = signInSchema.safeParse(values)
    if (!result.success) {
      return result.error.flatten().fieldErrors
    }

    try {
      const response = await fetch("/session", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: result.data.email,
          password: result.data.password,
        }),
      })

      const json = await response.json()
      if (json.success) {
        if (json.user != null) {
          $user.setKey("premium", json.user.premium)
          $user.setKey("forcedCurrency", json.user.forcedCurrency)
        }

        $user.setKey("signedIn", true)

        if (json.user?.premium) {
          window.location.reload()
        } else {
          replace("Payment")
        }

        return {}
      }

      if (json.errors != null) {
        return { ...json, [FORM_ERROR]: json.errors.join(" ") }
      }

      return json
    } catch (error) {
      return { [FORM_ERROR]: String(error) }
    }
  }, [])

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ submitting, pristine, submitError, handleSubmit }) => (
        <form className={css.signup} onSubmit={handleSubmit}>
          <FormSpy
            subscription={{ values: true }}
            onChange={(state) => {
              $user.setKey("email", state.values.email)
            }}
          />
          <h3 className={css.title} style={{ marginBottom: 12 }}>
            {t("your_account")}
          </h3>

          <p className={css.paragraph} style={{ marginBottom: 16 }}>
            {t("login_to_mtn")}
          </p>
          <fieldset className={css["signup-fieldset"]}>
            <Field<string>
              name="email"
              render={({ input, meta }) => (
                <label htmlFor="signin-email">
                  <input
                    {...input}
                    className={css["signup-input"]}
                    type="email"
                    autoComplete="email"
                    placeholder="Email address"
                    id="signin-email"
                    disabled={submitting}
                  />
                  {(meta.error || meta.submitError) && meta.touched && (
                    <div className={css["signup-field-error"]}>
                      {meta.error || meta.submitError}
                    </div>
                  )}
                </label>
              )}
            />
            <Field<string>
              name="password"
              render={({ input, meta }) => (
                <label htmlFor="signin-new-password">
                  <input
                    {...input}
                    className={css["signup-input"]}
                    type="password"
                    autoComplete="password"
                    placeholder="Password"
                    id="signin-new-password"
                    disabled={submitting}
                  />
                  {(meta.error || meta.submitError) && meta.touched && (
                    <div className={css["signup-field-error"]}>
                      {meta.error || meta.submitError}
                    </div>
                  )}

                  <p className={css["signup-field-note"]}>
                    {t("forgot_password")}{" "}
                    <a
                      href="/password/new"
                      onClick={(ev) => {
                        ev.preventDefault()
                        navigate("ForgotPassword")
                      }}
                    >
                      {t("recover_password")}
                    </a>
                  </p>
                </label>
              )}
            />
          </fieldset>

          {submitError && <div className={css["submit-error"]}>{submitError}</div>}

          <button
            className={css.button}
            style={{ marginBottom: 16 }}
            type="submit"
            disabled={submitting || pristine}
          >
            {t("continue")}
          </button>

          <p className={css["signup-offer"]}>
            {t("dont_have_account")}{" "}
            <a
              href="/sign_in"
              onClick={(ev) => {
                ev.preventDefault()
                replace("SignUp")
              }}
            >
              {t("sign_up")}
            </a>
          </p>
        </form>
      )}
    />
  )
}
